import { Component, OnInit } from '@angular/core';
import { ColumnMode } from '@swimlane/ngx-datatable';
import { ArchiveService } from 'src/app/services/archive/archive.service';
import Swal from 'sweetalert2';

@Component({
	selector: 'app-archive',
	templateUrl: './archive.component.html',
	styleUrls: ['./archive.component.scss'],
})
export class ArchiveComponent implements OnInit {
	ColumnMode = ColumnMode;

	archiveTabs = [
		{ key: 'leads', label: 'Leads' },
		{ key: 'prospects', label: 'Prospects' },
		{ key: 'dispo_log', label: 'Dispo Log' },
		{ key: 'login_history', label: 'Login History' },
	];
	activeTab = 'leads';

	columnsByTab: any = {
		leads: [
			{ name: 'ID', prop: 'id' },
			{ name: 'Creation Date', prop: 'lead_creation', type: 'date' },
			{ name: 'Agent', prop: 'generated_by'},
			{ name: 'Appt Date', prop: 'date_generated', type: 'date' },
			{ name: 'Phone', prop: 'primary_phone', type: 'phone' },
			{ name: 'Prospect', prop: 'prospect_company' },
			{ name: 'Status', prop: 'replacement_status' },
			{ name: 'Campaign Name', prop: 'campaign_name' },
		],

		prospects: [
			{ name: 'Company Name', prop: 'company_name' },
			{ name: 'Primary Phone', prop: 'primary_phone', type: 'phone' },
			{ name: 'Address', prop: 'address_1' },
			{ name: 'Created', prop: 'created', type: 'date' },
		],

		dispo_log: [
			{ name: 'Prospect', prop: 'prospect' },
			{ name: 'Primary Phone', prop: 'primary_phone', type: 'phone' },
			{ name: 'Dispo', prop: 'dispo' },
			{ name: 'Notes', prop: 'notes' },
		],

		login_history: [
			{ name: 'User', prop: 'user_id' },
			{ name: 'User Name', prop: 'user_name' },
			{ name: 'Login Date', prop: 'signin_time' },
			{ name: 'IP Address', prop: 'ip' },
		],
	};

	allColumnsByTab: Record<string, any[]> = {};

	get allColumns() {
		return this.allColumnsByTab[this.activeTab] || this.columnsByTab[this.activeTab] || [];
	}

	get defaultColumns() {
		return this.visibleColumnsByTab[this.activeTab] || this.allColumns;
	}

	visibleColumnsByTab: Record<string, any[]> = {};

	originalRows: any[] = [];
	archivedRows: any[] = [];

	startDate: string = '';
	endDate: string = '';

	constructor(private archiveSrv: ArchiveService) {}

	ngOnInit() {
		this.visibleColumnsByTab[this.activeTab] = [...this.columnsByTab[this.activeTab]];
		this.loadArchived(this.activeTab);
	}

	loadArchived(type: string) {
		this.archiveSrv.getArchive(type).subscribe({
			next: (res) => {
				if (res?.success && Array.isArray(res.body)) {
					this.originalRows = res.body;
				} else {
					this.originalRows = [];
				}

				this.archivedRows = [...this.originalRows];

				if (this.originalRows.length > 0) {
					this.generateDynamicColumns(this.originalRows[0]);
				}
			},
			error: (err) => {
				console.error('Error:', err);
				this.originalRows = [];
				this.archivedRows = [];
			},
		});
	}
	setActiveTab(tabKey: string) {
		this.activeTab = tabKey;

		if (!this.visibleColumnsByTab[tabKey]) {
			this.visibleColumnsByTab[tabKey] = [...this.columnsByTab[tabKey]];
		}

		this.loadArchived(tabKey);
	}
	restoreRecord(row: any) {
		Swal.fire({
			title: 'Are you sure?',
			text: 'This record will be restored.',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Yes, restore it!',
			cancelButtonText: 'Cancel',
			confirmButtonColor: '#3085d6',
			cancelButtonColor: '#d33',
		}).then((result) => {
			if (!result.isConfirmed) return;

			row.restoring = true;

			Swal.fire({
				title: 'Restoring...',
				text: 'Please wait',
				allowOutsideClick: false,
				didOpen: () => {
					Swal.showLoading();
				},
			});

			this.archiveSrv.restoreRecord(this.activeTab, row.id).subscribe({
				next: (res: any) => {
					row.restoring = false;
					Swal.close();

					if (res?.success && res?.body?.message) {
						Swal.fire({
							title: 'Restored!',
							text: res.body.message,
							icon: 'success',
							confirmButtonText: 'OK',
						}).then(() => {
							this.loadArchived(this.activeTab);
						});
					} else {
						Swal.fire('Error', 'Could not restore the record.', 'error');
					}
				},
				error: (err) => {
					row.restoring = false;
					Swal.close();
					Swal.fire('Error', 'Something went wrong.', 'error');
				},
			});
		});
	}
	updateFilter(event: any) {
		const val = event.target.value.toLowerCase();
		if (!val) {
			this.archivedRows = [...this.originalRows];
			return;
		}
		this.archivedRows = this.originalRows.filter((row: any) => {
			return Object.keys(row).some((key) => {
				return row[key]?.toString().toLowerCase().includes(val);
			});
		});
	}

	formatPhone(phone: string): string {
		if (!phone) return '';

		const digits = phone.replace(/\D/g, '');
		if (digits.length === 10) {
			return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
		}

		return phone;
	}

	isColumnVisible(col: any): boolean {
		const visible = this.visibleColumnsByTab[this.activeTab] || [];
		return visible.some((v) => v.prop === col.prop);
	}

	hideColumn(column: any) {
		let visible = this.visibleColumnsByTab[this.activeTab] || [];

		const isVisible = this.isColumnVisible(column);

		if (isVisible) {
			visible = visible.filter((c) => c.prop !== column.prop);
		} else {
			visible.push(column);
		}

		this.visibleColumnsByTab[this.activeTab] = this.allColumns.filter((col) => visible.some((v) => v.prop === col.prop));
	}

	generateDynamicColumns(row: any) {
		const responseColumns = Object.keys(row).map((key) => ({
			name: this.formatColumnName(key),
			prop: key,
		}));

		const predefined = this.columnsByTab[this.activeTab] || [];

		const mergedColumns = responseColumns.map((col) => {
			const match = predefined.find((p) => p.prop === col.prop);
			return match ? match : col;
		});

		this.allColumnsByTab[this.activeTab] = mergedColumns;
	}
	formatColumnName(key: string): string {
		return key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
	}

	searchByDate() {
		if (!this.startDate || !this.endDate) {
			alert('Please select both dates');
			return;
		}

		this.archiveSrv.getArchiveByDate(this.activeTab, this.startDate, this.endDate).subscribe({
			next: (res: any) => {
				if (res?.success && Array.isArray(res.body)) {
					this.originalRows = res.body;
					this.archivedRows = [...this.originalRows];

					if (this.originalRows.length > 0) {
						this.generateDynamicColumns(this.originalRows[0]);
					}
				} else {
					this.originalRows = [];
					this.archivedRows = [];
				}
			},
			error: (err) => {
				console.error('Error:', err);
			},
		});
	}

	resetFilter() {
		this.startDate = '';
		this.endDate = '';

		this.loadArchived(this.activeTab);
	}
}
